// THE INVITE POLICY — the one client rule for "may this person invite, at
// which roles, and whose role may they change, to what". Every control in the
// invite panel (the email form, its role select, the member-row role selects,
// the no-permission caption) reads this ONE function; nothing in the panel
// restates a role table of its own.
//
// Written model: workspace/docs/INVITE-PERMISSIONS-MODEL.md.
//
// IT MIRRORS THE SERVER, rule for rule (server main, 2026-10-08) — so the
// panel never offers a control the server refuses:
//
//   ORG (routes/organizations.js + OrganizationController)
//     POST  /:orgId/invitations        requireOrgRole(ORG_MGMT_ROLES)
//       createInvitation               role ∈ INVITABLE_ORG_ROLES
//                                      rank(caller) >= rank(role)
//                                      a PERSONAL org is refused (400)
//     POST  /:orgId/invitations {kind: 'link'}   (INVITE-MAGIC-LINK-1)
//       createLinkInvitation           the same gate and the same ceiling,
//                                      AND role <= maintainer by its resolved
//                                      base tier (400 link_role_too_high);
//                                      <= 25 open links per org (409
//                                      too_many_open_links — the panel shows
//                                      that refusal; it keeps no count)
//     PATCH /:orgId/members/:memberId  requireOrgRole(ORG_MGMT_ROLES)
//       updateMember                   new role != owner (transfer only)
//                                      co-owner only when the caller is owner
//                                      rank(caller) >= rank(target's CURRENT)
//   PROJECT (routes/projects.js + ProjectMemberController)
//     POST  /:projectId/invite         MANAGE_MEMBERS (admin, owner)
//       inviteMember                   inviter role ∈ {admin, owner}
//                                      admin only when the inviter is owner
//                                      owner never (one owner; transfer)
//     PATCH /:projectId/members/:id    MANAGE_MEMBERS
//       updateMemberRole               role ∈ {guest, editor, admin, owner}
//                                      owner only by an owner
//
// Where the panel is STRICTER than the server it says so below; it is never
// looser. Stricter, on purpose:
//   - a new org role is capped at the caller's own rank (the server checks
//     only the target's CURRENT rank, so a maintainer could lift a member to
//     admin through the API — that gap belongs to the server, not to a menu);
//   - owner rows and your own row are read-only here (ownership moves by
//     transfer; a self-demotion from a dropdown is a foot-gun);
//   - `owner` is never offered (the panel does not transfer ownership).
//
// ONE SOURCE FOR THE CALLER'S ROLE:
//   org      root.org (the active org row) → root.orgs row → the JWT claims
//            (`org_role`, `is_owner`). A row may carry `baseTier` (a custom
//            role resolved to its tier); a custom role WITHOUT a tier is a
//            known role the client cannot rank → no authority shown.
//   project  root.projectRole, plus `hasProjectPermission('inviteMembers')`
//            when the element carries it (the embed answers false there).
//
// LINK ROLES: `linkRoles` is `inviteRoles` capped at maintainer — a magic
// link is a bearer credential (whoever opens it first joins), so it never
// grants admin, whoever makes it. The magic-link select reads it; the email
// form keeps `inviteRoles`.
//
// UNKNOWN vs KNOWN: while the role is still unresolved (boot, an org switch)
// the invite form STAYS — an owner must never lose the control during
// hydration — but it offers only the roles every inviter may grant (org:
// contributor + member; project: guest + editor). Member-row role selects
// stay read-only until the role is known. A KNOWN role that may not invite
// gets no form at all, and the panel says why (`reason`).
//
// Self-contained by design: every table is declared INSIDE the function. It
// is reached through `el.call('invitePolicy', s)` and stringified for
// platform delivery, so a module-scope const read from inside it would read
// as undefined post-frank (FA204).
export function invitePolicy(s) {
  const el = this
  const root =
    (s && s.root) || (el && typeof el.getRootState === 'function' && el.getRootState()) || {}

  const LABEL = {
    owner: 'members.role.owner',
    'co-owner': 'members.role.coOwner',
    admin: 'members.role.admin',
    maintainer: 'members.role.maintainer',
    member: 'members.role.member',
    contributor: 'members.role.contributor',
    editor: 'members.role.editor',
    guest: 'members.role.guest',
    viewer: 'members.role.viewer'
  }
  const opt = (value) => ({ value, labelKey: LABEL[value] })
  const me = String(root.currentUserEmail || '').toLowerCase()
  const isSelf = (m) => !!me && String((m && m.email) || '').toLowerCase() === me
  const none = (context, reason) => ({
    context,
    role: null,
    resolved: true,
    mayInvite: false,
    reason,
    inviteRoles: [],
    linkRoles: [],
    defaultRole: null,
    mayManage: false,
    editRolesFor: () => [],
    labelKey: (r) => LABEL[r] || null
  })

  // ── PROJECT ────────────────────────────────────────────────────────
  if (root.projectId) {
    const MGMT = ['admin', 'owner']
    const role = root.projectRole || null
    const perm =
      el && typeof el.hasProjectPermission === 'function'
        ? !!el.hasProjectPermission('inviteMembers')
        : true
    if (!role) {
      return {
        ...none('project', null),
        resolved: false,
        mayInvite: perm,
        reason: perm ? null : 'noPermission',
        inviteRoles: perm ? ['guest', 'editor'].map(opt) : [],
        defaultRole: perm ? 'guest' : null
      }
    }
    const manage = MGMT.includes(role) && perm
    if (!manage) return { ...none('project', 'noPermission'), role }
    const inviteRoles = ['guest', 'editor', 'admin']
      .filter((r) => r !== 'admin' || role === 'owner')
      .map(opt)
    const EDITABLE = ['guest', 'editor', 'admin']
    return {
      ...none('project', null),
      role,
      mayInvite: true,
      inviteRoles,
      defaultRole: 'guest',
      mayManage: true,
      editRolesFor: (m) => {
        if (!m || !m.memberId || isSelf(m)) return []
        if (!EDITABLE.includes(m.role)) return []
        return EDITABLE.map(opt)
      }
    }
  }

  // ── ORG ────────────────────────────────────────────────────────────
  const orgId = root.jwtClaims && root.jwtClaims.workspace_org_id
  if (!orgId) return none(null, null)

  const RANK = { contributor: 1, member: 2, maintainer: 3, admin: 4, 'co-owner': 5, owner: 6 }
  const MGMT = ['owner', 'co-owner', 'admin', 'maintainer']
  const INVITABLE = ['contributor', 'member', 'maintainer', 'admin']
  const EDITABLE = ['contributor', 'member', 'maintainer', 'admin', 'co-owner']

  const isActive = (o) => !!o && (!(o.id || o._id) || String(o.id || o._id) === String(orgId))
  const fromRoot = isActive(root.org) ? root.org : null
  const fromList =
    (root.orgs || []).find((o) => o && String(o.id || o._id) === String(orgId)) || null
  const row = fromRoot || fromList
  if (row && row.type === 'personal') return none('org', 'personalOrg')

  const claims = root.jwtClaims || {}
  const rawRole =
    (fromRoot && fromRoot.role) ||
    (fromList && fromList.role) ||
    claims.org_role ||
    (claims.is_owner === true ? 'owner' : null)
  const tierOf = (r, b) => (RANK[b] ? b : RANK[r] ? r : null)
  const tier = tierOf(rawRole, (fromRoot && fromRoot.baseTier) || (fromList && fromList.baseTier))

  if (!rawRole) {
    return {
      ...none('org', null),
      resolved: false,
      mayInvite: true,
      reason: null,
      inviteRoles: ['contributor', 'member'].map(opt),
      linkRoles: ['contributor', 'member'].map(opt),
      defaultRole: 'member'
    }
  }
  if (!tier || !MGMT.includes(tier))
    return { ...none('org', 'noPermission'), role: tier || rawRole }

  const rank = RANK[tier]
  // The server's LINK_INVITE_MAX_ROLE: the highest role a magic link grants.
  const LINK_MAX = RANK.maintainer
  return {
    ...none('org', null),
    role: tier,
    mayInvite: true,
    inviteRoles: INVITABLE.filter((r) => RANK[r] <= rank).map(opt),
    linkRoles: INVITABLE.filter((r) => RANK[r] <= rank && RANK[r] <= LINK_MAX).map(opt),
    defaultRole: 'member',
    mayManage: true,
    editRolesFor: (m) => {
      if (!m || !m.memberId || isSelf(m)) return []
      const targetTier = tierOf(m.role, m.tier)
      if (!targetTier || RANK[targetTier] > rank) return []
      const choices = EDITABLE.filter(
        (r) => RANK[r] <= rank && (r !== 'co-owner' || tier === 'owner')
      )
      // A custom role (or one this caller may not set) has no option that
      // shows it — the row stays read-only rather than display a lie.
      if (!choices.includes(m.role)) return []
      return choices.map(opt)
    }
  }
}

// May this caller share a MAGIC LINK here? The one answer for every surface
// that offers one (the shared MagicLinkInInvite section, the shell's Email |
// Magic link switch). Four checks: an org context, a caller who may invite,
// at least one role a link may grant (the policy's `linkRoles`, at most
// maintainer), and an SDK that can create a link. Reached through
// `el.call('magicLinkOffered', s)` (`this` is the element).
export function magicLinkOffered(s) {
  const el = this
  const p = el.call('invitePolicy', s)
  return (
    p.context === 'org' &&
    p.mayInvite &&
    p.linkRoles.length > 0 &&
    !!el.sdk &&
    typeof el.sdk.createOrgInvitationLink === 'function'
  )
}

// The roles the current caller may grant in an invite — the policy's
// `inviteRoles`, kept as its own entry for the callers that only need the
// list. An empty list means "may not invite here".
export function grantableInviteRoles(s) {
  const p = this && typeof this.call === 'function' ? this.call('invitePolicy', s) : null
  return (p && p.inviteRoles) || []
}
